<template>
  <div>
        <span title="More information" v-on:click="aboutPage = true" class="about">
            <font-awesome-icon icon="info"/>
        </span>
    <transition name="fade">
      <div v-if="aboutPage" v-on:click.self="aboutPage = false" class="aboutPage">
        <div class="aboutPage-content">
          <span v-on:click="aboutPage = false" style="float: right"> <font-awesome-icon icon="times"/></span>
          <h1>Transportation Access to Core Services</h1>
          <p>How well do transportation systems and land use work together to connect Southeast Michigan
            residents with the places they need or want to go each day? Understanding this relationship helps
            communities prioritize improvements and investments that better serve everyone.</p>
          <p>To support this understanding, SEMCOG conducted a comprehensive analysis of transportation access
            to core services across the seven-county region. The analysis measures travel times between
            households and core services—such as <strong>jobs, healthcare, education, parks and recreation,
              food</strong>, and
            fixed-route transit—for various demographic groups and across four modes of travel: <strong>walking,
              biking</strong>
            (accounting for biking comfort levels), <strong>fixed-route transit</strong>, and <strong>driving</strong>
            and illustrates <strong style="text-decoration: underline">potential
              access.</strong></p>
          <p>This interactive tool allows users to explore how access to these core services varies across
            Southeast Michigan, helping planners, policymakers, and residents identify where improvements can
            make the biggest difference.</p>
          <h2>Data</h2>
          <h3>Core Services Data</h3>
          <table>
            <tr>
              <th>Core Service</th>
              <th>Description</th>
              <th>Source</th>
            </tr>
            <tr>
              <td>Grocery Stores</td>
              <td>Retail locations that are capable of supporting
                a full week’s worth of grocery shopping. Included are supermarkets and ethnic grocery stores, while gas
                stations, liquor stores, and farmers markets are excluded from this definition.
              </td>
              <td>OpenStreetMap (OSM)</td>
            </tr>
            <tr>
              <td>Any Healthcare Facility</td>
              <td>Including at least one of the three core services including hospitals, community health centers, or
                urgent care facilities.
              </td>
              <td>Combined OSM and PolicyMap</td>
            </tr>
            <tr>
              <td>Hospitals</td>
              <td>Hospitals included in this accessibility analysis have an emergency department and/or trauma center
                and offer acute care services to patients arriving without prior appointments.
              </td>
              <td>OpenStreetMap (OSM)</td>
            </tr>
            <tr>
              <td>Community Health Centers</td>
              <td>Also known as Federally Qualified Health Centers (FQHC), these centers help increase access to crucial
                primary care by reducing barriers such as cost, lack of insurance, distance, and language for their
                patients.
              </td>
              <td>PolicyMap</td>
            </tr>
            <tr>
              <td>Urgent Care Facilities</td>
              <td>Offering immediate medical attention for non-life-threatening conditions, especially during evenings
                and weekends when regular physicians may be unavailable. These facilities accept walk-in patients (with
                some offering online check-in), and accept a range of insurance plans, including Medicare.
              </td>
              <td>OpenStreetMap (OSM)</td>
            </tr>
            <tr>
              <td>Pharmacies</td>
              <td>Retail locations that dispense prescription
                medications and may also offer over-the-counter drugs, basic health screenings,
                immunizations, and limited health consultations.
              </td>
              <td>OpenStreetMap (OSM)</td>
            </tr>
            <tr>
              <td>Parks (Local and School)</td>
              <td>Local Parks are defined as parks less than 100 acres in size. In addition to local parks, many
                schools—particularly elementary schools—
                offer park-like amenities such as playgrounds, athletic fields, and maintained green spaces that
                contribute to neighborhood recreation.
              </td>
              <td>SEMCOG Parks Dataset</td>
            </tr>
            <tr>
              <td>Regional Parks (>100 Acres)</td>
              <td>Parks greater than 100 acres in size. Includes Park entrance for vehicles and park entrance for
                bicyclists/pedestrians.
              </td>
              <td>SEMCOG Parks Dataset</td>
            </tr>
            <tr>
              <td>Childcare Facilities</td>
              <td>Licensed childcare homes, group homes and centers under
                the Michigan Department of Lifelong Education, Advancement, and Potential (MiLEAP).
              </td>
              <td>2024 <a target="_blank"
                          href="https://www.arcgis.com/home/item.html?id=a79c3b0caedf412599085941e2af91d4">State of
                Michigan</a> and 2025
                <a target="_blank"
                   href="https://semcog.maps.arcgis.com/home/item.html?id=c802ebe9d8204356acd28d95f7bb5fbd">State of
                  Ohio</a></td>
            </tr>
            <tr>
              <td>Schools K-8</td>
              <td>Public schools, charter schools,
                and alternative schools and does not include private schools or religious schools.
              </td>
              <td>State of Michigan’s schools <a target="_blank"
                                                 href="https://gisagocss.state.mi.us/arcgis/rest/services/CSS/Schools_Districts/MapServer/0">dataset</a>
              </td>
            </tr>
            <tr>
              <td>Schools 9-12</td>
              <td>Public high schools, charter schools,
                and alternative schools and does not include private schools or religious schools.
              </td>
              <td>State of Michigan’s schools <a target="_blank"
                                                 href="https://gisagocss.state.mi.us/arcgis/rest/services/CSS/Schools_Districts/MapServer/0">dataset</a>
              </td>
            </tr>
            <tr>
              <td>Libraries</td>
              <td>Public libraries with computers available and does not include university libraries or specialty
                libraries.
              </td>
              <td>OpenStreetMap (OSM)</td>
            </tr>
            <tr>
              <td>Community Colleges</td>
              <td>Total of 21 campuses associated with 8 community colleges in the region as well as 2 community
                colleges outside of the region.
              </td>
              <td><a target="_blank"
                     href="https://www.michigan.gov/reconnect/community-college/list-of-colleges#g=42.33143|-83.04575&o=Distance%2CAscending&distance=100">Michigan
                Reconnect</a></td>
            </tr>
            <tr>
              <td>American Job Centers</td>
              <td>American Job Centers provide job seekers with comprehensive support—including
                training referrals, career counseling, and job listings—all in one location, as established by the
                Workforce Innovation and Opportunity Act of 2014.
              </td>
              <td><a target="_blank" href="https://www.michiganworks.org/michigan-works-network">Michigan Works!</a>
              </td>
            </tr>
            <tr>
              <td>Fixed-Route Transit Stops</td>
              <td>This analysis includes eleven fixed-route transit systems operating in the region.
              </td>
              <td>2025 GTFS data</td>
            </tr>
            <tr>
              <td>Passenger Train Stations</td>
              <td>Amtrak Services including, Chicago – Grand Rapids/East Lansing – Port Huron/Detroit – Pontiac, and New
                York/Boston – Albany – Chicago Lake Shore Limited.
              </td>
              <td>2025 <a target="_blank"
                          href="https://www.amtrak.com/home.html?cmp=pdsrch-Non-Core+DMA%7CBrand%7CGoogle%7CExact_National_SEM_CV_NA_Q125_Q425_NA-google&gclsrc=aw.ds&gad_source=1&gad_campaignid=1679117511&gbraid=0AAAAADuWIyfOR2nSLfhrSf_bgvI1V4OJr&gclid=CjwKCAiAz_DIBhBJEiwAVH2XwOnMB4TzktbSr_3VKSeQCokyIP69EFOIC0guyoYNn-Mf-y6DtoIvtRoCRKUQAvD_BwE">Amtrak
                website</a></td>
            </tr>
            <tr>
              <td>Passenger Airport (DTW)</td>
              <td>Two terminals within the Detroit Metro Airport (DTW) including the North Terminal and McNamara
                Terminal.
              </td>
              <td>2025 <a target="_blank" href="https://www.metroairport.com/">DTW Website</a>
              </td>
            </tr>
            <tr>
              <td>Fire Stations</td>
              <td>Fire stations as well as volunteer fire stations.
              </td>
              <td><a target="_blank"
                     href="https://semcog.maps.arcgis.com/home/item.html?id=2c36dbb008844081b017da6fd3d0d28b&sublayer=2">Living
                atlas</a></td>
            </tr>
            <tr>
              <td>Jobs</td>
              <td>Total jobs including wage and salary jobs as well as self-employment from SEMCOG 2019 employment data
                prepared for 2050 regional forecast model
              </td>
              <td>2019 Employment Data for Regional Forecast</td>
            </tr>
          </table>
          <h3>Demographic Data</h3>
          <table>
            <tr>
              <th>Demographic layer (Land Parcel level)</th>
              <th>Description</th>
              <th>Source</th>
            </tr>
            <tr>
              <td>All Households</td>
              <td>Total households
              </td>
              <td>2020 Base Year Data for 2050 Regional Forecast</td>
            </tr>
            <tr>
              <td>All Population</td>
              <td>Total population
              </td>
              <td>2020 Base Year Data for 2050 Regional Forecast</td>
            </tr>
            <tr>
              <td>Transit-Dependent Households</td>
              <td>Households with no car or # of cars less than # of workers
              </td>
              <td>2020 Base Year Data for 2050 Regional Forecast</td>
            </tr>
            <tr>
              <td>Households with Seniors</td>
              <td>Households with senior person 65 years or older
              </td>
              <td>2020 Base Year Data for 2050 Regional Forecast</td>
            </tr>
            <tr>
              <td>Households with Children</td>
              <td>Households with children less than 17 years old
              </td>
              <td>2020 Base Year Data for 2050 Regional Forecast</td>
            </tr>
            <tr>
              <td>Low-income Households</td>
              <td>Household income is in the lowest quartile of the region
              </td>
              <td>2020 Base Year Data for 2050 Regional Forecast</td>
            </tr>
            <tr>
              <td>Minority-headed Households</td>
              <td>Household head is minority
              </td>
              <td>2020 Base Year Data for 2050 Regional Forecast</td>
            </tr>
            <tr>
              <td>Working Age Population</td>
              <td>Population age from 18 to 64
              </td>
              <td>2020 Base Year Data for 2050 Regional Forecast</td>
            </tr>
          </table>
          <br>
          <table>
            <tr>
              <th>Additional Demographic Data (Census Block Group)</th>
              <th>Description</th>
              <th>Source</th>
            </tr>
            <tr>
              <td>All Households</td>
              <td>Total households
              </td>
              <td>Census ACS 5-year Data 2019-2023</td>
            </tr>
            <tr>
              <td>All Population</td>
              <td>Total population
              </td>
              <td>Census ACS 5-year Data 2019-2023</td>
            </tr>
            <tr>
              <td>Transportation Access Focus Population</td>
              <td>Vulnerable populations in need of transit services as identified by a composite score from eight
                demographic groups (transit-dependent households, households in poverty, households with seniors,
                households with children, population with disability, households with persons 14 years and older who
                have limited English ability, minority-headed households, and female-headed households) relative to the
                regional average. See the report for detailed methodology.
              </td>
              <td>Census ACS 5-year Data 2019-2023</td>
            </tr>
            <tr>
              <td>Population with Disability</td>
              <td>Persons with any of the following disabilities, hearing, vision,
                cognitive, ambulatory, self-care, or independent living difficulty.
              </td>
              <td>Census ACS 5-year Data 2019-2023</td>
            </tr>
            <tr>
              <td>Households in Poverty</td>
              <td>Household in poverty status (household’s total income is less than the federal poverty threshold)
              </td>
              <td>Census ACS 5-year Data 2019-2023</td>
            </tr>
            <tr>
              <td>Single-Parent Households</td>
              <td>Single-Parent Households
              </td>
              <td>Census ACS 5-year Data 2019-2023</td>
            </tr>
            <tr>
              <td>Female-Headed Households</td>
              <td>Number of female-headed households (family household where the female householder has no spouse living
                in the home)
              </td>
              <td>Census ACS 5-year Data 2019-2023</td>
            </tr>
            <tr>
              <td>Households Receiving SNAP</td>
              <td>Number of households receiving SNAP (Supplemental Nutrition Assistance Program)
              </td>
              <td>Census ACS 5-year Data 2019-2023</td>
            </tr>
            <tr>
              <td>Households with Limited English Proficiency</td>
              <td>Number of households with persons 14 years and older who have limited English ability
              </td>
              <td>Census ACS 5-year Data 2019-2023</td>
            </tr>
          </table>
          <h3>Network data</h3>
          <div>
            <ul>
              <li><strong>Walk Network:</strong>
                <ul>
                  <li>This network is developed from the OpenStreetMap (OSM) road network, utilizing the OSMnx Python
                    library to extract walkable features.
                  </li>
                </ul>
              </li>
              <li><strong>Bike Network:</strong>
                <ul>
                  <li><strong>Network Without Comfort Level Considerations:</strong> This bike network is based on the
                    original OSM road
                    network, with bikeable features extracted using the OSMnx library.
                  </li>
                  <li><strong>Network With Comfort Level Considerations:</strong>
                    This network incorporates data from SEMCOG’s Bicycle and Pedestrian Mobility Network, which
                    categorizes bike routes into four comfort levels (as detailed in the "SEMCOG Bicycle and Pedestrian
                    Mobility Plan"). SEMCOG assigns travel time discounts to OSM bikeable roads based on the comfort
                    level as follows:
                    <ul>
                      <li>Level 1. Comfortable for Most People: 1.0 (no discount)</li>
                      <li>Level 2. Comfortable for Many People: 1.2</li>
                      <li>Level 3. Comfortable for Some People: 1.6</li>
                      <li>Level 4. Comfortable for Few People: 2.2</li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li><strong>Transit Network:</strong>
                <ul>
                  <li>SEMCOG has compiled General Transit Feed Specification (GTFS) data from 11 fixed-route transit
                    services in the Southeast Michigan region, including:
                    <ul>
                      <li>Ann Arbor Area Transportation Authority (AAATA; TheRide)</li>
                      <li>Blue Water Area Transit (BWAT)</li>
                      <li>City of Detroit Department of Transportation (DDOT)</li>
                      <li>Detroit People Mover (DPM)</li>
                      <li>Lake Erie Transit (LET)</li>
                      <li>Michigan Flyer (serving East Lansing, Whitmore Lake, Ann Arbor, and Detroit Metropolitan
                        Airport)
                      </li>
                      <li>Regional Transit Authority (RTA)’s Detroit Air Express (DAX)</li>
                      <li>Regional Transit Authority (RTA)’s Detroit Ann Arbor Express (D2A2)</li>
                      <li>Regional Transit Authority (RTA)’s QLINE</li>
                      <li>Suburban Mobility Authority for Regional Transportation (SMART)</li>
                      <li>University of Michigan Transit</li>
                    </ul>
                  </li>
                  <li>
                    SEMCOG then integrated all transit routes with walkable OSM roads to create a seamlessly connected
                    transit service network. This network ensures coverage within a 10-minute walk of any transit
                    station and includes comprehensive transit service schedules, service headways, and transfer times,
                    forming the basis for evaluating transit accessibility.
                  </li>
                </ul>
              </li>
              <li><strong>Drive Network:</strong>
                <ul>
                  <li>To establish a fully connected drivable network, SEMCOG merged three major road networks: the
                    SEMCOG travel demand forecast model network, the TMACOG (Toledo Metropolitan Area Council of
                    Governments) travel demand forecast model network, and the major roads from the Michigan Geographic
                    Framework encompassing counties surrounding the SEMCOG region. This network features both peak-hour
                    and non-peak-hour driving times, facilitating accessibility evaluations across different times of
                    day.
                  </li>
                </ul>
              </li>
            </ul>
          </div>
          <h3>Complementary data</h3>
          <div>
            <ul>
              <li><strong>School District Boundaries</strong></li>
              <ul>
                <li>The School District data displayed in this application is from the Michigan Geographic Framework
                  (MGF) Base Map. However, it is in no way the official source for school boundary information and
                  should be used for general reference only. The MGF ISD and School District data is derived from US
                  Census TIGER data which is the official source.
                </li>
              </ul>
              <li><strong>Fixed-Route Transit Routes Layer:</strong> The Fixed-Route Transit Routes layer includes
                eleven fixed-route
                transit systems,
                <ol>
                  <li>Ann Arbor Area Transportation Authority (AAATA; TheRide)</li>
                  <li>Blue Water Area Transit (BWAT)</li>
                  <li>City of Detroit Department of Transportation (DDOT)</li>
                  <li>Detroit People Mover (DPM)</li>
                  <li>Lake Erie Transit (LET)</li>
                  <li>Michigan Flyer (serving East Lansing, Whitmore Lake, Ann Arbor, and Detroit Metropolitan
                    Airport)
                  </li>
                  <li>Regional Transit Authority (RTA)’s Detroit Air Express (DAX)</li>
                  <li>Regional Transit Authority (RTA)’s Detroit Ann Arbor Express (D2A2)</li>
                  <li>Regional Transit Authority (RTA)’s QLINE</li>
                  <li>Suburban Mobility Authority for Regional Transportation (SMART)</li>
                  <li>University of Michigan Transit</li>
                </ol>
              </li>
              <li><strong>Land Use Context Layer</strong>
                <ul>
                  <li>The <strong>Land Use Context layer</strong> is sourced from the <a target="_blank"
                                                                                         href="https://www.semcog.org/regional-planning-and-projects/transportation-mobility/mobility-options/multimodal-tool/">SEMCOG
                    and MDOT Multimodal Tool</a> (developed by
                    SEMCOG and MDOT in 2024, the Multi Modal tool allows users plan, design, and evaluate multimodal
                    street cross-sections for each of five travel modes: pedestrian, bike, transit, auto, and freight).
                    The Land Use Context layer includes five categories,
                    <ol>
                      <li>Main Streets (Existing and Aspirational)</li>
                      <li>Urban Corridors and Walkable Thoroughfares (Existing and Aspirational)</li>
                      <li>Suburban Corridors</li>
                      <li>Small Town Hamlet or Village Commercial Corridors</li>
                      <li>Rural Corridors</li>
                    </ol>
                  </li>
                  <li>Please see the <a target="_blank"
                                        href="https://www.semcog.org/wp-content/uploads/2025/08/MultimodalToolUserGuideDec2024.pdf">Multimodal
                    User Guide</a> (page 7-20), for additional information related to each
                    category including Description, Data Sources, and different Metrics (such as Land Use, Block
                    Length/Approximate Size).
                  </li>
                </ul>
              </li>
            </ul>
          </div>
          <h2>Methodology</h2>
          <div>
            <ul>
              <li><strong>Nearest Accessibility:</strong> measures proximity of key destinations across demographic
                groups (e.g., the
                share of households within a 10-minute walk of a local park).
                <ul>
                  <li>The nearest accessibility method was used to assess walk, bike, fixed-route transit, and drive
                    access to all core services except for access to Jobs.
                  </li>
                </ul>
              </li>
              <li><strong>Cumulative Accessibility:</strong> measures the total number of opportunities or destinations
                that can be
                reached within a specific time or distance (e.g., the number of jobs accessible within 30 minutes by
                transit during the morning peak).
                <ul>
                  <li>
                    The cumulative method was used to assess walk, bike (considering bike comfort levels), fixed-route
                    transit and drive access to Jobs.
                  </li>
                </ul>
              </li>
              <li><strong>Gravity-Based Accessibility:</strong> accounts for the fact that closer destinations are more
                attractive than
                distant ones, enabling community-level comparisons (e.g. if a resident can reach 10 jobs within 1 mile,
                those jobs have a full impact on accessibility. However, if jobs are 10 miles away, their influence
                diminishes, and so on).
                <ul>
                  <li>
                    The Gravity-based accessibility was used to assess fixed-route transit and drive access to Jobs.
                  </li>
                </ul>
              </li>
            </ul>
          </div>
          <h2>Acknowledgments and Limitations</h2>
          <p>This analysis applies multiple measures to evaluate <strong
              style="text-decoration: underline">potential</strong> accessibility across different travel modes,
            time thresholds, and household characteristics. While these measures provide a robust and consistent basis
            for comparing access across the region, it is important to acknowledge that actual travel behavior and use
            may differ from modeled accessibility. Factors such as individual travel preferences, affordability,
            physical ability, trip purpose, weather, lack of knowledge on how to ride transit, safety perceptions, and
            real-time transit reliability can all influence how residents choose—or are able—to travel.
          </p>
          <p><strong style="text-decoration: underline">Access to Jobs:</strong> This study does not include a
            competition-based analysis of access to jobs and does not
            account for job type or workers’ skills and education. These factors may influence the actual accessibility
            experienced by different populations and should be considered in future research. SEMCOG offers <a
                target="_blank"
                href="https://maps.semcog.org/laborshed/">Laborshed in
              Southeast Michigan Online Tool</a> which shows where Southeast Michigan’s <strong>workers by Industry
              sector</strong> and
            <strong>occupation</strong> live, work, and their commutes between both locations. It also provides an
            estimate of nonworking
            residents – or people who are either not employed or looking for work. The data are structured by census
            tract.</p>
          <h2>Links</h2>
          <div>
            <a target="_blank" href="https://www.semcog.org/wp-content/uploads/2026/01/QuickFacts_TranportationAccesstoCoreServices.pdf">Transportation Access to Core Services Quick Facts</a>
          </div>
          <div>
            <a target="_blank" href="https://www.semcog.org/wp-content/uploads/2026/01/TransportationAccesstoCoreServices_Report_2025.pdf">Transportation Access to Core Services Report</a>
          </div>
          <div style="margin-bottom: 25px"><a
              href="mailto:makari@semcog.org?Subject=Transporation Access to Core Services" target="_top"> <br>
            Questions? Email Beheshteh Makari.</a></div>
        </div>
      </div>
    </transition>

  </div>

</template>

<script>
export default {
  name: "AboutPage",
  data: function () {
    return {
      aboutPage: false,
    }
  },
}
</script>

<style scoped>

.about {
  float: right;
  padding: 5px;
}

.aboutPage {
  position: fixed;
  background: rgba(46, 60, 67, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  opacity: 1;
  overflow: hidden;
}

.aboutPage-content {
  background: white;
  max-width: 80%;
  border: #fffcfc solid 2px;
  border-radius: 10px;
  padding: 10px 30px;
  max-height: 80%;
  overflow: auto;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 1s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.fade-enter-active .aboutPage-content, .fade-leave-active .aboutPage-content {
  transition: transform 1s;
}

.fade-enter .aboutPage-content, .fade-leave-to .aboutPage-content {
  transform: translate(-50vw, -50vh) scale(0);
}

.fade-enter-to .aboutPage-content, .fade-leave-to .aboutPage-content {
  overflow: hidden;
}

table {
  border-collapse: collapse;
  align-self: center;
  width: 75%;
}

td, th {
  border: 1px solid #999;
  padding: 0.2rem;
}
</style>
